/**
 * The Master's Certificate (plan docs 06 and 16).
 *
 * The exam is the last node of Section 10: fifty questions drawn from every
 * domain at the top of the difficulty range, plus play tasks. Eighty-five per
 * cent passes and awards the certificate, the graduation gown and a badge on
 * the profile.
 *
 * The arithmetic lives here rather than in the screen so the pass mark is one
 * number in one place, and so that both the exam screen and the certificate
 * screen agree about what a grade means.
 */

/** The lesson node that is the exam. */
export const CERTIFICATE_LESSON_ID = 's10.u6.l1';

/** The certificate's own id, which the graduation gown's unlock refers to. */
export const CERTIFICATE_ID = 'master';

/** Fifty questions, as doc 06 specifies. */
export const CERTIFICATE_QUESTIONS = 50;

/** Eighty-five per cent. */
export const CERTIFICATE_PASS_MARK = 0.85;

export type CertificateGrade = 'pass' | 'merit' | 'distinction';

export interface CertificateResult {
  /** 0..1. */
  score: number;
  correct: number;
  total: number;
  passed: boolean;
  /** Only meaningful when passed. */
  grade: CertificateGrade;
  /** How many more answers a near miss needed. */
  shortBy: number;
}

/**
 * Grades an attempt.
 *
 * Merit and distinction are above the pass mark rather than replacing it: the
 * certificate is the same certificate, and the grade is printed on it.
 */
export function gradeCertificate(
  correct: number,
  total = CERTIFICATE_QUESTIONS,
): CertificateResult {
  if (!Number.isFinite(total) || total <= 0) {
    throw new Error(`A certificate attempt needs questions, got ${total}`);
  }
  const clamped = Math.max(0, Math.min(total, Math.round(correct)));
  const score = clamped / total;
  const passed = score >= CERTIFICATE_PASS_MARK;
  const needed = Math.ceil(CERTIFICATE_PASS_MARK * total);
  return {
    score,
    correct: clamped,
    total,
    passed,
    grade: score >= 0.97 ? 'distinction' : score >= 0.93 ? 'merit' : 'pass',
    shortBy: passed ? 0 : needed - clamped,
  };
}

/** "92% — merit", the line the certificate and the result card both print. */
export function certificateGradeLine(result: CertificateResult): string {
  const percent = Math.round(result.score * 100);
  if (!result.passed) return `${percent}%`;
  return result.grade === 'pass' ? `${percent}%` : `${percent}% — ${result.grade}`;
}

/** The domains the exam draws from, for the certificate's small print. */
export const CERTIFICATE_DOMAINS: readonly string[] = [
  'notation',
  'rhythm',
  'pitch',
  'harmony',
  'ear',
  'form',
  'acoustics',
  'instrument',
  'composition',
];
